import { useMemo } from 'react'

/**
 * useLawyerData
 *
 * Deriva datos para la vista "Abogado" a partir del contexto de cálculo existente.
 * Produce un veredicto, vías legales aplicables, hallazgos y el próximo paso sugerido.
 *
 * @param {Object} options
 * @param {Object}  options.contextoDictamen  — resultado de composeDictamenContext
 * @param {Object}  options.expediente        — datos del titular
 * @param {Object}  options.activeRules       — resultado de strategicEvaluator
 * @param {Array}   options.rows              — filas del historial laboral
 * @returns {Object} datos formateados para la vista abogado
 */
export default function useLawyerData({ contextoDictamen, expediente, activeRules, rows }) {
  return useMemo(() => {
    if (!contextoDictamen || !expediente) {
      return getDefaultValue()
    }

    const resumen = contextoDictamen.resumenEjecutivo || {}
    const caseMeta = activeRules?.caseMeta || {}
    const rowsList = Array.isArray(rows) ? rows : []

    // --- Datos financieros ---
    const haberActual = resumen.haberActualAnses || 0
    const haberReajustado = resumen.haberBolsilloReajustado || 0
    const diferenciaMensual = resumen.diferencialMensual || 0
    const retroactivoEstimado = resumen.retroactivoTotal || 0
    const incrementoPct = resumen.incrementoMensualPct || 0

    // --- Gaps ---
    const gapsPre94 = rowsList.filter(r =>
      r.requiresRevision && r.periodoKey < '1994-07'
    ).length
    const gapsPost94 = rowsList.filter(r =>
      r.requiresRevision && r.periodoKey >= '1994-07'
    ).length

    // --- Veredicto ---
    const veredicto = buildVeredicto({
      diferenciaMensual,
      haberActual,
      haberReajustado,
      activeRules,
    })

    // --- Vías legales ---
    const viasLegales = buildViasLegales({
      contextoDictamen,
      expediente,
      activeRules,
      rowsList,
      gapsPre94,
      gapsPost94,
      diferenciaMensual,
    })

    // --- Hallazgos ---
    const hallazgos = buildHallazgos({
      contextoDictamen,
      expediente,
      activeRules,
      rowsList,
      gapsPre94,
      gapsPost94,
      haberActual,
      haberReajustado,
    })

    // --- Próximo paso sugerido ---
    const proximoPaso = buildProximoPaso({
      viasLegales,
      veredicto,
      expediente,
      activeRules,
    })

    return {
      veredicto,
      haberActual,
      haberReajustado,
      diferenciaMensual,
      incrementoPct,
      retroactivoEstimado,
      gapsPre94,
      gapsPost94,
      viasLegales,
      hallazgos,
      proximoPaso,
    }
  }, [contextoDictamen, expediente, activeRules, rows])
}

/* ------------------------------------------------------------------ */
/*  Veredicto                                                          */
/* ------------------------------------------------------------------ */

function buildVeredicto({ diferenciaMensual, haberActual, haberReajustado, activeRules }) {
  const caseMeta = activeRules?.caseMeta || {}

  // Determinar tipo de veredicto
  let tipo = 'viable'     // 'viable' | 'revision' | 'noviable'
  let titulo = ''
  let descripcion = ''
  const tags = []

  if (diferenciaMensual <= 0 && haberActual > 0) {
    tipo = 'noviable'
    titulo = 'Caso no viable — sin margen de reajuste'
    descripcion = `El haber actual de $${formatMoney(haberActual)} es igual o superior al haber calculado de $${formatMoney(haberReajustado)}. No se detecta margen para un reajuste favorable.`
    tags.push({ tipo: 'red', texto: '✕ Sin margen' })
  } else if (diferenciaMensual > 0 && diferenciaMensual < 50000) {
    tipo = 'revision'
    titulo = 'Caso con margen limitado — evaluar si justifica el trámite'
    descripcion = `La diferencia mensual de $${formatMoney(diferenciaMensual)} es positiva pero reducida. Evaluar si el retroactivo estimado de $${formatMoney(haberActual > 0 ? diferenciaMensual * 12 : 0)} justifica iniciar el trámite.`
    tags.push({ tipo: 'gold', texto: `📈 Margen: +$${formatMoney(diferenciaMensual)}/mes` })
    tags.push({ tipo: 'blue', texto: '⚖️ Evaluar costo-beneficio' })
  } else if (diferenciaMensual > 0 && caseMeta.needsSubsanacion) {
    tipo = 'revision'
    titulo = 'Requiere revisión — pero con oportunidad de reajuste'
    descripcion = `El haber que cobra hoy ($${formatMoney(haberActual)}) está por debajo del haber calculado ($${formatMoney(haberReajustado)}). La diferencia de +$${formatMoney(diferenciaMensual)} mensuales justifica iniciar el trámite, aunque hay puntos a resolver (años faltantes, períodos sin registro).`
    tags.push({ tipo: 'green', texto: '✅ Viable con gestión' })
    tags.push({ tipo: 'gold', texto: `📈 Diferencia: +$${formatMoney(diferenciaMensual)}/mes` })
  } else if (diferenciaMensual > 0) {
    tipo = 'viable'
    titulo = 'Caso viable — con oportunidad real de reajuste'
    descripcion = `El haber que cobra hoy ($${formatMoney(haberActual)}) está por debajo de lo que le correspondería. El sistema calculó un haber reajustado de $${formatMoney(haberReajustado)}. La diferencia de +$${formatMoney(diferenciaMensual)} mensuales justifica iniciar el trámite.`
    tags.push({ tipo: 'green', texto: '✅ Viable' })
    tags.push({ tipo: 'gold', texto: `📈 Diferencia: +$${formatMoney(diferenciaMensual)}/mes` })
  } else {
    tipo = 'revision'
    titulo = 'Requiere revisión — faltan datos para determinar viabilidad'
    descripcion = 'No se cuenta con datos suficientes para determinar la viabilidad del caso. Verificar que el historial laboral esté completo y que se haya ingresado el haber actual.'
    tags.push({ tipo: 'gold', texto: '⚠️ Faltan datos' })
  }

  return { tipo, titulo, descripcion, tags }
}

/* ------------------------------------------------------------------ */
/*  Vías legales                                                       */
/* ------------------------------------------------------------------ */

function buildViasLegales({ contextoDictamen, expediente, activeRules, rowsList, gapsPre94, diferenciaMensual }) {
  const vias = []
  const caseMeta = activeRules?.caseMeta || {}
  const diagnostico = contextoDictamen?.diagnosticoCalculo || {}
  const resumen = contextoDictamen?.resumenEjecutivo || {}
  const prestaciones = resumen.prestaciones || {}

  // --- 1. Reajuste por ISBIC/RIPTE (Fallo Pinto/Cardozo) ---
  const falloActual = (expediente.fallo || 'ISBIC').toUpperCase()
  if (falloActual !== 'ISBIC' && falloActual !== 'RIPTE') {
    vias.push({
      id: 'isbic',
      impacto: 'alto',
      titulo: 'Reajuste por ISBIC — Fallo "Pinto" (CSJN 2020)',
      impactoEstimado: determinarImpacto(diferenciaMensual, 'alto'),
      descripcion: `La Corte Suprema en "Pinto" (2020) estableció que los haberes deben actualizarse por ISBIC (Índice de Salarios Básicos) y no por el índice general. Actualmente el caso está configurado con el fallo "${expediente.fallo || 'IPC'}". Aplicando ISBIC el haber podría aumentar significativamente.`,
      fundamentos: 'CSJN "Pinto" (2020) · CSJN "Cardozo" (2022) · A 14.290',
    })
  }

  // --- 2. Fórmula Docente (Decreto 137/05) ---
  const esDocente = caseMeta.specialRegimeKey === 'DOCENTE'
  const yaAplicoDocente = diagnostico.isDocenteScenario || prestaciones.regimenDocenteAplicado
  if (esDocente && !yaAplicoDocente) {
    vias.push({
      id: 'docente',
      impacto: 'alto',
      titulo: 'Fórmula Docente — Decreto 137/05',
      impactoEstimado: '$400K a $700K/mes',
      descripcion: 'El sistema detectó actividad docente en el historial. El Decreto 137/05 permite usar el 82% del último sueldo bruto como haber en vez del promedio histórico. Esta vía no se aplicó en el cálculo actual porque no se confirmó el cargo de cese.',
      fundamentos: 'Decreto 137/05 · Ley 24.241 · Fallo "Márquez" (docentes)',
      observacion: `Se detectó fused row con posible cargo docente — revisar sueldoBrutoCargoCese = $${formatMoney(expediente?.datosDocentes?.sueldoBrutoCargoCese || 0)}`,
    })
  }

  // --- 3. Moratoria (Ley 27.705) ---
  if (caseMeta.needsSubsanacion || caseMeta.requiresMoratoria) {
    const aniosFaltantes = Math.max(0, 30 - Math.round(caseMeta.aporteAnios || 0))
    vias.push({
      id: 'moratoria',
      impacto: 'medio',
      titulo: 'Regularización de años sin aportes — Ley 27.705',
      impactoEstimado: '$30K a $100K/mes',
      descripcion: `El titular necesita completar años de aportes. La Ley 27.705 (moratoria vigente) permite regularizar esos períodos. No solo habilita el acceso a la jubilación, sino que suma años al cálculo del haber, lo que puede mejorar el promedio si se regularizan años con salarios altos.`,
      fundamentos: 'Ley 27.705 · Decreto 475/2021 (tareas de cuidado)',
    })
  }

  // --- 4. Reconstrucción de períodos pre-94 ---
  if (gapsPre94 > 0) {
    vias.push({
      id: 'pre94',
      impacto: 'medio',
      titulo: 'Reconstrucción de salarios previos a 1994',
      impactoEstimado: '$60K a $100K/mes',
      descripcion: `Hay ${gapsPre94} períodos anteriores a 1994 sin salario registrado digitalmente. Si se reconstruyen con recibos o certificaciones de servicios, esos períodos aportan al cómputo de la PBU y al promedio general. Es una vía complementaria que no requiere litigio — se puede gestionar administrativamente.`,
      fundamentos: 'Ley 24.241 Art. 24 · Decreto 137/05',
    })
  }

  // --- 5. Tasa de interés (Fallo Badosa) ---
  vias.push({
    id: 'tasa',
    impacto: 'bajo',
    titulo: 'Tasa de interés activa — Fallo "Badosa"',
    impactoEstimado: '$10K a $25K/mes (más en retroactivo)',
    descripcion: 'Sobre el retroactivo adeudado, corresponde aplicar la tasa activa del Banco Nación según "Badosa" (CSJN). Si se aplicó una tasa menor, el retroactivo puede incrementarse significativamente.',
    fundamentos: 'CSJN "Badosa" (2021) · CSJN "Ponce" (2022)',
  })

  return vias
}

/* ------------------------------------------------------------------ */
/*  Hallazgos                                                          */
/* ------------------------------------------------------------------ */

function buildHallazgos({
  contextoDictamen, expediente, activeRules, rowsList,
  gapsPre94, gapsPost94, haberActual, haberReajustado,
}) {
  const hallazgos = []
  const caseMeta = activeRules?.caseMeta || {}
  const diagnostico = contextoDictamen?.diagnosticoCalculo || {}

  // --- Índice de actualización ---
  const falloActual = (expediente.fallo || 'ISBIC').toUpperCase()
  if (falloActual === 'IPC' || falloActual === 'IPC_GENERAL') {
    hallazgos.push({
      icon: '🔴',
      titulo: 'Se aplicó un índice de actualización menor al que corresponde',
      detalle: 'La liquidación actual usa el índice general (IPC). Según el fallo "Pinto" de la CSJN, corresponde aplicar ISBIC. Corregir esto puede aumentar el haber significativamente.',
    })
  }

  // --- Docente no aplicado ---
  if (caseMeta.specialRegimeKey === 'DOCENTE' && !diagnostico.isDocenteScenario) {
    hallazgos.push({
      icon: '🔴',
      titulo: 'No se aplicó la fórmula docente a pesar de registrarse actividad docente',
      detalle: `El sistema detectó cargos docentes en el historial (${caseMeta.specialRegimeLabel}). Corresponde evaluar si el cargo de cese habilita el Decreto 137/05.`,
    })
  }

  // --- Períodos pre-94 ---
  if (gapsPre94 > 0) {
    hallazgos.push({
      icon: '🟡',
      titulo: `${gapsPre94} períodos pre-94 sin salario registrado`,
      detalle: 'Son años anteriores a 1994 donde ANSES no tiene digitalizados los salarios. No impide la jubilación pero deja afuera del cálculo esos años. Reconstruirlos con recibos o certificaciones puede mejorar el haber.',
    })
  }

  // --- Años faltantes ---
  if (caseMeta.needsSubsanacion) {
    const aniosFaltantes = Math.max(0, 30 - Math.round(caseMeta.aporteAnios || 0))
    hallazgos.push({
      icon: '🟡',
      titulo: `${aniosFaltantes} año${aniosFaltantes !== 1 ? 's' : ''} sin aportes registrados`,
      detalle: 'Períodos sin registrar en la historia laboral. Correspondería evaluar si hubo trabajo no registrado o si aplica moratoria (Ley 27.705) para regularizarlos.',
    })
  }

  // --- Régimen especial detectado ---
  if (caseMeta.specialRegimeDetected && caseMeta.specialRegimeKey !== 'LEY_24241' && caseMeta.specialRegimeKey !== 'DOCENTE') {
    hallazgos.push({
      icon: '🔵',
      titulo: `Régimen especial detectado: ${caseMeta.specialRegimeLabel}`,
      detalle: 'El sistema detectó un régimen especial en el historial. Verificar que se esté aplicando la normativa específica correspondiente.',
    })
  }

  // --- Fallback aplicado (general desde especial) ---
  if (diagnostico.fallbackGeneralApplied) {
    hallazgos.push({
      icon: '🟡',
      titulo: 'Se aplicó fórmula general como fallback desde régimen especial',
      detalle: 'El cálculo usó la fórmula general porque no se cumplían las condiciones del régimen especial detectado. Revisar si corresponde forzar la aplicación del régimen especial.',
    })
  }

  // --- Sin hallazgos relevantes ---
  if (hallazgos.length === 0) {
    hallazgos.push({
      icon: '🔵',
      titulo: 'No se detectaron irregularidades en la liquidación actual',
      detalle: 'El cálculo se realizó correctamente con los datos disponibles. Si el haber actual es superior al calculado, corresponde explorar vías legales para cerrar la brecha.',
    })
  }

  return hallazgos
}

/* ------------------------------------------------------------------ */
/*  Próximo paso                                                       */
/* ------------------------------------------------------------------ */

function buildProximoPaso({ viasLegales, veredicto, expediente, activeRules }) {
  const caseMeta = activeRules?.caseMeta || {}
  const falloActual = (expediente.fallo || 'ISBIC').toUpperCase()

  // Priorizar la vía de mayor impacto que no sea la tasa de interés
  const viaPrincipal = viasLegales.find(v => v.impacto !== 'bajo')

  if (viaPrincipal) {
    return {
      titulo: viaPrincipal.titulo,
      descripcion: `Iniciar por esta vía — es la de mayor impacto detectada para este caso. ${viaPrincipal.observacion || ''}`,
    }
  }

  if (veredicto.tipo === 'noviable') {
    return {
      titulo: 'Evaluar si existen otras vías no detectadas',
      descripcion: 'El cálculo no muestra margen de reajuste con los datos actuales. Revisar si hay información adicional del titular que no se haya cargado en el sistema.',
    }
  }

  return {
    titulo: 'Completar los datos del caso',
    descripcion: 'Faltan datos para determinar el próximo paso. Verificar que el historial laboral, el haber actual y los datos del titular estén completos.',
  }
}

/* ------------------------------------------------------------------ */
/*  Utilities                                                          */
/* ------------------------------------------------------------------ */

function determinarImpacto(diferencia, nivel) {
  if (nivel === 'alto') {
    if (diferencia > 500000) return '+$500K a $900K/mes'
    if (diferencia > 200000) return '+$200K a $500K/mes'
    return '+$100K a $200K/mes'
  }
  if (nivel === 'medio') {
    return '+$30K a $100K/mes'
  }
  return '+$10K a $50K/mes'
}

function formatMoney(value) {
  if (typeof value !== 'number' || isNaN(value)) return '0'
  return Math.round(value).toLocaleString('es-AR')
}

function getDefaultValue() {
  return {
    veredicto: { tipo: 'revision', titulo: 'Sin datos', descripcion: '', tags: [] },
    haberActual: 0,
    haberReajustado: 0,
    diferenciaMensual: 0,
    incrementoPct: 0,
    retroactivoEstimado: 0,
    gapsPre94: 0,
    gapsPost94: 0,
    viasLegales: [],
    hallazgos: [],
    proximoPaso: { titulo: '', descripcion: '' },
  }
}
